import { useEffect, useMemo, useState } from "react";
import { CaretLeftIcon, CaretRightIcon } from "@phosphor-icons/react";

import Layout from "../../components/Layout";
import EntityHeader from "../../components/crm/EntityHeader";
import { ErrorBanner, SecondaryButton } from "../../components/crm/Form";
import WorkOrderCard from "../../components/ops/WorkOrderCard";
import api, { formatApiError } from "../../lib/api";

function startOfWeek(d) {
  const x = new Date(d);
  const day = x.getDay(); // 0 = Sun
  const diff = (day + 6) % 7; // make Mon = 0
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - diff);
  return x;
}

function fmtIsoDate(d) {
  return d.toISOString().slice(0, 10);
}

function fmtDayLabel(d) {
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

const UNASSIGNED_KEY = "__unassigned__";

export default function DispatchBoard() {
  const [anchor, setAnchor] = useState(() => startOfWeek(new Date()));
  const [data, setData] = useState({
    techs: [],
    work_orders: [],
  });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const days = useMemo(() => {
    const out = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(anchor);
      d.setDate(anchor.getDate() + i);
      out.push(d);
    }
    return out;
  }, [anchor]);

  const dateFrom = fmtIsoDate(days[0]);
  const dateTo = fmtIsoDate(days[6]);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const { data: d } = await api.get("/dispatch/board", {
        params: { date_from: dateFrom, date_to: dateTo },
      });
      setData(d);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo]);

  const grouped = useMemo(() => {
    const out = {};
    const ensure = (techId, dayKey) => {
      if (!out[techId]) out[techId] = {};
      if (!out[techId][dayKey]) out[techId][dayKey] = [];
      return out[techId][dayKey];
    };
    for (const w of data.work_orders || []) {
      if (!w.scheduled_start) continue;
      const dayKey = w.scheduled_start.slice(0, 10);
      const techId = w.assigned_lead_tech_user_id || UNASSIGNED_KEY;
      ensure(techId, dayKey).push(w);
      if (
        w.assigned_assistant_user_id &&
        w.assigned_assistant_user_id !== w.assigned_lead_tech_user_id
      ) {
        ensure(w.assigned_assistant_user_id, dayKey).push(w);
      }
    }
    return out;
  }, [data.work_orders]);

  const shift = (n) => {
    const d = new Date(anchor);
    d.setDate(anchor.getDate() + n * 7);
    setAnchor(startOfWeek(d));
  };

  return (
    <Layout title="Dispatch Board" subtitle="Week view">
      <EntityHeader
        overline="dispatch"
        title="Dispatch Board"
        subtitle="Schedule and assign work across the week."
      />

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <SecondaryButton
          onClick={() => shift(-1)}
          data-testid="dispatch-prev-week"
        >
          <CaretLeftIcon size={14} weight="bold" /> Prev Week
        </SecondaryButton>
        <SecondaryButton
          onClick={() => setAnchor(startOfWeek(new Date()))}
          data-testid="dispatch-this-week"
        >
          This Week
        </SecondaryButton>
        <SecondaryButton
          onClick={() => shift(1)}
          data-testid="dispatch-next-week"
        >
          Next Week <CaretRightIcon size={14} weight="bold" />
        </SecondaryButton>
        <div className="font-data text-[10px] uppercase tracking-[0.22em] text-slate-500 ml-auto tabular">
          {fmtDayLabel(days[0])} → {fmtDayLabel(days[6])}
        </div>
      </div>

      <ErrorBanner message={err} />

      {/* Header row */}
      <div className="overflow-x-auto bg-white border border-slate-200 rounded-sm">
        <div
          className="grid"
          style={{
            gridTemplateColumns: "180px repeat(7, minmax(150px, 1fr))",
          }}
          data-testid="dispatch-grid"
        >
          {/* Top-left tech header */}
          <div className="bg-slate-900 text-white px-3 py-2 font-data text-[10px] uppercase tracking-[0.18em] sticky left-0 z-10">
            Technician
          </div>
          {days.map((d) => (
            <div
              key={d.toISOString()}
              className="bg-slate-900 text-white px-3 py-2 font-data text-[10px] uppercase tracking-[0.18em] border-l border-slate-700"
            >
              {fmtDayLabel(d)}
            </div>
          ))}

          {/* Unassigned row */}
          <DispatchRow
            label="Unassigned"
            sublabel="needs scheduling"
            techId={UNASSIGNED_KEY}
            days={days}
            grouped={grouped}
          />

          {/* Tech rows */}
          {(data.techs || []).map((t) => (
            <DispatchRow
              key={t.id}
              label={t.name}
              sublabel={t.role.replace("_", " ")}
              techId={t.id}
              days={days}
              grouped={grouped}
            />
          ))}
        </div>
      </div>

      {!loading && (data.work_orders || []).length === 0 && (
        <div
          data-testid="dispatch-empty"
          className="mt-4 text-xs text-slate-500"
        >
          No scheduled work orders in this week.
        </div>
      )}
    </Layout>
  );
}

function DispatchRow({ label, sublabel, techId, days, grouped }) {
  const techCells = grouped[techId] || {};
  return (
    <>
      <div
        className="bg-slate-50 border-t border-slate-200 px-3 py-3 sticky left-0 z-10"
        data-testid={`dispatch-row-${techId}`}
      >
        <div className="text-sm font-medium text-slate-900 truncate">
          {label}
        </div>
        <div className="font-data text-[10px] uppercase tracking-[0.18em] text-slate-500 mt-0.5">
          {sublabel}
        </div>
      </div>
      {days.map((d) => {
        const k = d.toISOString().slice(0, 10);
        const wos = techCells[k] || [];
        return (
          <div
            key={k}
            className="border-t border-l border-slate-200 p-2 min-h-[100px] bg-white"
            data-testid={`dispatch-cell-${techId}-${k}`}
          >
            <div className="space-y-2">
              {wos.map((w) => (
                <WorkOrderCard key={w.id} wo={w} compact />
              ))}
            </div>
          </div>
        );
      })}
    </>
  );
}
